import { ArrowLeft, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAuth } from '@/providers/AuthProvider';
import { CancelNfseDialog } from '@/components/fiscal/CancelNfseDialog';
import { ConferenceCompare } from '@/components/fiscal/ConferenceCompare';
import { FiscalStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { MarkPaidDialog } from '@/components/fiscal/MarkPaidDialog';
import { NfseHistoryList } from '@/components/fiscal/NfseHistoryList';
import { OcrPreview } from '@/components/fiscal/OcrPreview';
import { ReleaseDivergenceDialog } from '@/components/fiscal/ReleaseDivergenceDialog';
import { ReportUpload } from '@/components/fiscal/ReportUpload';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { useFiscalMutations, useFiscalRecord } from '@/features/fiscal/hooks';
import { useCancelNfse } from '@/features/nfse/hooks';
import { ApiError } from '@/lib/api';

export default function FiscalRecordPage() {
  const { recordId } = useParams<{ recordId: string }>();
  const id = recordId ?? null;
  const query = useFiscalRecord(id);
  const mutations = useFiscalMutations(id);
  const toast = useToast();

  const [releaseOpen, setReleaseOpen] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const cancelNfse = useCancelNfse(id);
  const { user } = useAuth();
  const certificateMissing = !user?.hasCertificate;

  if (query.isLoading) {
    return <p className="text-sm text-(--color-fg-muted)">Carregando…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="text-sm text-(--color-error)">
        Registro fiscal não encontrado.
      </p>
    );
  }

  const { record, service, reports, nfseIssuances } = query.data;
  const canUpload =
    record.status === 'AWAITING_REPORT' || record.status === 'IN_REVIEW';
  const canRelease = record.status === 'DIVERGENT';
  const canMarkPaid = record.status === 'NFSE_SENT';
  const canCancel =
    record.status === 'NFSE_EMITTED' || record.status === 'NFSE_SENT';
  const hasPending = reports.some(
    (r) => r.ocrStatus === 'PENDING' || r.ocrStatus === 'PROCESSING',
  );

  const notes = notesDraft ?? record.notes ?? '';

  const onError = (err: unknown): void => {
    const msg =
      err instanceof ApiError ? err.message : 'Não foi possível concluir a ação';
    toast.show({ title: msg, variant: 'error' });
  };

  const handleUpload = async (file: File): Promise<void> => {
    try {
      await mutations.uploadReport.mutateAsync(file);
      toast.show({ title: 'Relatório enviado. OCR em andamento.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleAdjust = async (value: number): Promise<void> => {
    try {
      await mutations.updateReportedValue.mutateAsync({ reportedValue: value });
      toast.show({ title: 'Valor ajustado.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleRelease = async (reason: string): Promise<void> => {
    try {
      await mutations.releaseEmission.mutateAsync(reason);
      setReleaseOpen(false);
      toast.show({ title: 'Emissão liberada.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleMarkPaid = async (paidAt: string): Promise<void> => {
    try {
      await mutations.markPaid.mutateAsync(paidAt);
      setPaidOpen(false);
      toast.show({ title: 'Registro marcado como pago.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleCancel = async (reason: string): Promise<void> => {
    try {
      await cancelNfse.mutateAsync(reason);
      setCancelOpen(false);
      toast.show({ title: 'NF cancelada.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleRetry = async (): Promise<void> => {
    try {
      await mutations.retryNfse.mutateAsync();
      toast.show({ title: 'Nova tentativa enfileirada.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  const handleSaveNotes = async (): Promise<void> => {
    if (notesDraft == null) return;
    if (record.reportedValue == null) {
      toast.show({
        title: 'Ajuste ou confirme o valor do relatório antes de salvar observações.',
        variant: 'error',
      });
      return;
    }
    try {
      await mutations.updateReportedValue.mutateAsync({
        reportedValue: record.reportedValue,
        notes: notesDraft,
      });
      setNotesDraft(null);
      toast.show({ title: 'Observações salvas.', variant: 'success' });
    } catch (err) {
      onError(err);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to="/fiscal"
          className="inline-flex items-center gap-1 text-sm text-(--color-fg-muted) hover:text-(--color-fg)"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Voltar
        </Link>
      </div>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {service ? (
            <span
              aria-hidden
              className="inline-block h-4 w-4 rounded-full"
              style={{ backgroundColor: service.color }}
            />
          ) : null}
          <div>
            <h1 className="font-display text-2xl font-semibold">
              {service?.name ?? 'Serviço removido'}
            </h1>
            <p className="text-sm text-(--color-fg-muted)">
              Mês de competência:{' '}
              {new Date(record.referenceMonth).toLocaleDateString('pt-BR', {
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
        <FiscalStatusBadge status={record.status} />
      </header>

      {/* Seção: Anexos */}
      <Card>
        <CardHeader>
          <CardTitle>Anexos</CardTitle>
        </CardHeader>
        {canUpload ? (
          <ReportUpload
            onUpload={handleUpload}
            isUploading={mutations.uploadReport.isPending}
          />
        ) : (
          <p className="text-sm text-(--color-fg-muted)">
            Uploads bloqueados no status atual.
          </p>
        )}
        {reports.length > 0 ? (
          <ul className="mt-3 flex flex-col divide-y divide-(--color-border)">
            {reports.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <span className="truncate">{r.gcsObjectPath.split('/').pop()}</span>
                <span className="text-xs text-(--color-fg-muted)">
                  {r.source === 'EMAIL_INBOUND' ? 'E-mail' : 'Upload manual'} ·{' '}
                  {(r.sizeBytes / 1024).toFixed(0)} KB
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      {/* Seção: OCR */}
      <OcrPreview
        reports={reports}
        reportedValue={record.reportedValue}
        onAdjust={handleAdjust}
        isAdjusting={mutations.updateReportedValue.isPending}
        disabled={!canUpload && record.status !== 'DIVERGENT'}
      />

      {hasPending ? (
        <p className="text-sm text-(--color-fg-muted)">
          OCR em processamento — os dados atualizam automaticamente.
        </p>
      ) : null}

      {/* Seção: Conferência */}
      <ConferenceCompare
        computedProduction={record.computedProduction}
        reportedValue={record.reportedValue}
        tolerancePct={record.tolerancePctAtTime}
        diffAbs={record.diffAbs}
        diffPct={record.diffPct}
      />

      {certificateMissing ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1">
            Cadastre seu certificado digital (A1) para liberar a emissão da NFS-e.{' '}
            <Link to="/certificates" className="font-medium underline hover:no-underline">
              Cadastrar certificado
            </Link>
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {canRelease ? (
          <Button
            onClick={() => setReleaseOpen(true)}
            disabled={certificateMissing}
            title={
              certificateMissing
                ? 'Cadastre o certificado digital para liberar a emissão'
                : undefined
            }
          >
            <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden />
            Liberar emissão
          </Button>
        ) : null}
        {canMarkPaid ? (
          <Button variant="secondary" onClick={() => setPaidOpen(true)}>
            Marcar como pago
          </Button>
        ) : null}
        {canCancel ? (
          <Button variant="ghost" onClick={() => setCancelOpen(true)}>
            <XCircle className="mr-1 h-4 w-4" aria-hidden />
            Cancelar NF
          </Button>
        ) : null}
      </div>

      {/* Seção: NFS-e */}
      <NfseHistoryList
        issuances={nfseIssuances}
        onRetry={handleRetry}
        isRetrying={mutations.retryNfse.isPending}
        retryDisabled={certificateMissing}
        retryDisabledReason={
          certificateMissing
            ? 'Cadastre o certificado digital antes de tentar novamente'
            : undefined
        }
      />

      {/* Seção: Observações */}
      <Card>
        <CardHeader>
          <CardTitle>Observações</CardTitle>
        </CardHeader>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Notas livres sobre a conferência, contato com o hospital, etc."
          className="w-full rounded-md border border-(--color-border) bg-(--color-surface) p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
        />
        {notesDraft != null && notesDraft !== (record.notes ?? '') ? (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={handleSaveNotes}
              disabled={mutations.updateReportedValue.isPending}
            >
              Salvar observações
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNotesDraft(null)}>
              Descartar
            </Button>
          </div>
        ) : null}
      </Card>

      <ReleaseDivergenceDialog
        open={releaseOpen}
        onOpenChange={setReleaseOpen}
        onConfirm={handleRelease}
        isSubmitting={mutations.releaseEmission.isPending}
      />
      <MarkPaidDialog
        open={paidOpen}
        onOpenChange={setPaidOpen}
        onConfirm={handleMarkPaid}
        isSubmitting={mutations.markPaid.isPending}
      />
      <CancelNfseDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={handleCancel}
        isSubmitting={cancelNfse.isPending}
      />
    </div>
  );
}
